import { FC, useCallback, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../../../components/custom_components/FlexRow'
import PerText from '../../../components/custom_components/Text'
import { Card, Checkbox, Col } from 'antd'
import { CompanyTiny, KudosFilterInput, EmployeeSearch, Country, CompanyScope } from '../../../types'
import PerButtonCircle from '../../../components/custom_components/ButtonCircle'
import { useDescriptionSearch } from '../hooks/useDescriptionSearch'
import { EmployeeSearchSelect } from './EmployeeSearchSelect'
import { CompanySearchSelect } from './CompanySearchSelect'
import { CountrySelect } from './CountrySelect'
import { MonthYearFilter } from './MonthYearFilter'
import { useCompanySearch } from '../../../graphql/company/hooks/useCompanySearch'
import { useCountriesList } from '../../../graphql/countrySettings/hooks/useCountriesList'
import { CreateKudosModalWithEmployeeSelect } from '../../../components/custom_components/CreateKudosModal/CreateKudosModalWithEmployeeSelect'
import { useActiveEmployeeSearch } from '../../employees/EmployeeSearch/useActiveEmployeeSearch'
import { KUDOS_DASHBOARD_PAGE_SIZE } from '../../../graphql/kudos/util'

interface KudosInfoBoxProps {
  filters: KudosFilterInput
  onFiltersChange: (filters: KudosFilterInput) => void
  selectedEmployee: EmployeeSearch | null
  selectedAuthor: EmployeeSearch | null
  setSelectedEmployee: (employee: EmployeeSearch | null) => void
  setSelectedAuthor: (author: EmployeeSearch | null) => void
}

export const KudosHRInfoBox: FC<KudosInfoBoxProps> = ({
  filters,
  onFiltersChange,
  selectedAuthor,
  setSelectedAuthor,
  selectedEmployee,
  setSelectedEmployee,
}) => {
  const { t } = useTranslation()

  const employeeSearch = useActiveEmployeeSearch(true)
  const authorSearch = useActiveEmployeeSearch(true)
  const descriptionSearch = useDescriptionSearch((value) => {
    onFiltersChange({
      ...filters,
      searchTerm: value,
    })
  })

  // Initialize description search with existing filter
  useEffect(() => {
    if (filters.searchTerm && descriptionSearch.description === '') {
      descriptionSearch.initializeDescription(filters.searchTerm)
    }
  }, [filters.searchTerm, descriptionSearch])

  const companySearch = useCompanySearch(CompanyScope.All)
  const countrySearch = useCountriesList()

  const [selectedEmployeeCompany, setSelectedEmployeeCompany] = useState<CompanyTiny | null>(null)
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
  const [isCreateKudosModalVisible, setIsCreateKudosModalVisible] = useState(false)

  // Employee option selection handler
  const handleEmployeeOptionClick = useCallback(
    (employee: EmployeeSearch) => {
      setSelectedEmployee(employee)
      onFiltersChange({
        ...filters,
        employeeId: employee.id,
      })
      employeeSearch.handleClear()
    },
    [filters, onFiltersChange, employeeSearch, setSelectedEmployee],
  )

  // Author option selection handler
  const handleAuthorOptionClick = useCallback(
    (author: EmployeeSearch) => {
      setSelectedAuthor(author)
      onFiltersChange({
        ...filters,
        authorId: author.id,
        filterByMe: false, // Decheck filterByMe when selecting author
      })
      authorSearch.handleClear()
    },
    [filters, onFiltersChange, authorSearch, setSelectedAuthor],
  )

  // Handle clearing employee selection
  const handleEmployeeClear = useCallback(() => {
    setSelectedEmployee(null)
    onFiltersChange({
      ...filters,
      employeeId: undefined,
    })
    employeeSearch.handleClear()
  }, [filters, onFiltersChange, employeeSearch, setSelectedEmployee])

  // Handle clearing author selection
  const handleAuthorClear = useCallback(() => {
    setSelectedAuthor(null)
    onFiltersChange({
      ...filters,
      authorId: undefined,
    })
    authorSearch.handleClear()
  }, [filters, onFiltersChange, authorSearch, setSelectedAuthor])

  // Handle employee company selection
  const handleEmployeeCompanyOptionClick = useCallback(
    (company: CompanyTiny) => {
      setSelectedEmployeeCompany(company)
      onFiltersChange({
        ...filters,
        employeeCompanyId: company.id,
      })
      companySearch.handleClear()
    },
    [filters, onFiltersChange, companySearch],
  )

  // Handle clearing company selection
  const handleCompanyClear = useCallback(() => {
    setSelectedEmployeeCompany(null)
    onFiltersChange({
      ...filters,
      employeeCompanyId: undefined,
    })
    companySearch.handleClear()
  }, [filters, onFiltersChange, companySearch])

  // Handle country selection
  const handleCountrySelect = useCallback(
    (country: Country | null) => {
      setSelectedCountry(country)
      onFiltersChange({
        ...filters,
        country: country || undefined,
      })
    },
    [filters, onFiltersChange],
  )

  const handleFilterByMeChange = (e: any) => {
    // Clear selected author when checking "Show Only My Kudos"
    if (e.target.checked) {
      setSelectedAuthor(null)
      authorSearch.handleClear()
    }

    onFiltersChange({
      ...filters,
      filterByMe: e.target.checked,
      authorId: e.target.checked ? undefined : filters.authorId, // Clear authorId when checking filterByMe
    })
  }

  const handleClearFilters = () => {
    setSelectedEmployee(null)
    setSelectedAuthor(null)
    setSelectedEmployeeCompany(null)
    setSelectedCountry(null)
    descriptionSearch.handleDescriptionClear()
    employeeSearch.handleClear()
    authorSearch.handleClear()
    companySearch.handleClear()
    onFiltersChange({
      filterByMe: false,
      employeeCompanyId: undefined,
      country: undefined,
      startDate: undefined,
      endDate: undefined,
      pagination: {
        offset: 0,
        limit: KUDOS_DASHBOARD_PAGE_SIZE,
      },
    })
  }

  const hasActiveFilters = () => {
    return !!(
      filters.searchTerm ||
      filters.authorId ||
      filters.employeeId ||
      filters.employeeCompanyId ||
      filters.country ||
      filters.startDate ||
      filters.endDate ||
      filters.filterByMe
    )
  }

  return (
    <Card
      bordered={false}
      style={{
        backgroundColor: 'white',
        boxShadow: 'none',
        borderRadius: '8px',
        height: '205px',
      }}
    >
      <PerFlexRow direction="column" justify="space-between" className="fullHeight" width="fullWidth">
        {/* Header with title and add buttons */}
        <PerFlexRow justify="space-between" align="middle" width="fullWidth">
          <PerFlexRow align="middle">
            <PerText appearance="tile-heading">{t('kudos.management.title')}</PerText>
          </PerFlexRow>
          <PerButtonCircle
            icon="add_stroke"
            color="primary"
            variant="pastel"
            onClick={() => setIsCreateKudosModalVisible(true)}
            tooltip={t('kudos.management.createNewWithEmployee')}
            data-e2e="create-kudos-with-employee-button"
          />
        </PerFlexRow>

        <PerText mb="xxs" fontSize="s">
          {t('kudos.management.dashboardDisclaimer')}
        </PerText>

        {/* Filters - Grid Layout */}

        <PerFlexRow>
          <PerFlexRow width="fullWidth" mb="xs">
            {/* Employee Name */}
            <Col span={5}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                  {t('kudos.management.filters.employeeName')}
                </PerText>
                <EmployeeSearchSelect
                  searchTerm={employeeSearch.searchTerm}
                  onSearch={employeeSearch.handleSearch}
                  onClear={handleEmployeeClear}
                  employees={employeeSearch.employees}
                  loading={employeeSearch.loading}
                  shouldShowOptions={employeeSearch.shouldShowOptions}
                  onOptionClick={handleEmployeeOptionClick}
                  placeholder={t('kudos.management.filters.employeeNamePlaceholder')}
                  selectedEmployee={selectedEmployee}
                  size={'small'}
                />
              </PerFlexRow>
            </Col>

            {/* Employee Company */}
            <Col span={5}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                  {t('kudos.management.filters.employeeCompany')}
                </PerText>
                <CompanySearchSelect
                  searchTerm={companySearch.searchTerm}
                  onSearch={companySearch.handleSearch}
                  onClear={handleCompanyClear}
                  companies={companySearch.companies}
                  loading={companySearch.loading}
                  shouldShowOptions={companySearch.shouldShowOptions}
                  onOptionClick={handleEmployeeCompanyOptionClick}
                  placeholder={t('kudos.management.filters.employeeCompanyPlaceholder')}
                  selectedCompany={selectedEmployeeCompany}
                />
              </PerFlexRow>
            </Col>

            {/* Country Filter */}
            <Col span={5}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                  {t('kudos.management.filters.country')}
                </PerText>
                <CountrySelect
                  countries={countrySearch.countries}
                  selectedCountry={selectedCountry}
                  onCountrySelect={handleCountrySelect}
                  placeholder={t('kudos.management.filters.countryPlaceholder')}
                  loading={countrySearch.loading}
                  onDropdownVisibleChange={(open) => {
                    if (open && countrySearch.countries.length === 0) {
                      countrySearch.loadCountries() // Load countries when dropdown opens
                    }
                  }}
                />
              </PerFlexRow>
            </Col>

            {/* From Date Filter */}
            <Col span={4}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                  {t('kudos.management.filters.fromDate')}
                </PerText>
                <MonthYearFilter filters={filters} onFiltersChange={onFiltersChange} filterType="from" />
              </PerFlexRow>
            </Col>

            {/* To Date Filter */}
            <Col span={4}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                  {t('kudos.management.filters.toDate')}
                </PerText>
                <MonthYearFilter filters={filters} onFiltersChange={onFiltersChange} filterType="to" />
              </PerFlexRow>
            </Col>
          </PerFlexRow>

          <PerFlexRow width="fullWidth">
            {/* Author Name */}
            <Col span={5}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxxs">
                  {t('kudos.management.filters.authorName')}
                </PerText>
                <EmployeeSearchSelect
                  searchTerm={authorSearch.searchTerm}
                  onSearch={authorSearch.handleSearch}
                  onClear={handleAuthorClear}
                  employees={authorSearch.employees}
                  loading={authorSearch.loading}
                  shouldShowOptions={authorSearch.shouldShowOptions}
                  onOptionClick={handleAuthorOptionClick}
                  placeholder={t('kudos.management.filters.authorNamePlaceholder')}
                  selectedEmployee={selectedAuthor}
                  size={'small'}
                />
              </PerFlexRow>
            </Col>

            {/* Show kudos assigned by me */}
            <Col span={9}>
              <PerFlexRow direction="column">
                <PerText appearance="label-small" fontWeight="bold" transform="uppercase" mb="xxs">
                  {t('kudos.management.filters.showOnlyMine')}
                </PerText>
                <PerFlexRow align="middle">
                  <Checkbox checked={filters.filterByMe || false} onChange={handleFilterByMeChange} />
                  <PerText fontSize="s" color="secondary" ml="xxs">
                    {t('kudos.management.filters.showOnlyMine')}
                  </PerText>
                </PerFlexRow>
              </PerFlexRow>
            </Col>

            <Col span={10}>
              <PerFlexRow justify="end" align="bottom" className="fullHeight">
                <PerButtonCircle
                  color="error"
                  icon="delete_fill"
                  variant="pastel"
                  onClick={handleClearFilters}
                  disabled={!hasActiveFilters()}
                  tooltip={t('kudos.management.filters.clear')}
                />
              </PerFlexRow>
            </Col>
          </PerFlexRow>
        </PerFlexRow>
      </PerFlexRow>

      {/* Create Kudos Modal with Employee Select */}
      <CreateKudosModalWithEmployeeSelect
        visible={isCreateKudosModalVisible}
        closeModal={() => setIsCreateKudosModalVisible(false)}
        onSuccess={() => {
          setIsCreateKudosModalVisible(false)
        }}
        filters={{ ...filters, pagination: { offset: 0, limit: KUDOS_DASHBOARD_PAGE_SIZE } }}
        replyerView={false}
      />
    </Card>
  )
}

export default KudosHRInfoBox
